import { useState, useMemo } from 'react'
import { generateClient } from 'aws-amplify/api'
import { updateFlagStatus, addFlagNote } from '../graphql/mutations'
import { format } from 'date-fns'
import './FlagModal.css'

function FlagModal({ flag, onClose, onUpdate }) {
  const [submitting, setSubmitting] = useState(false)
  const [newNote, setNewNote] = useState('')

  const client = useMemo(() => {
    try {
      return generateClient()
    } catch (error) {
      console.error('Error initializing GraphQL client:', error)
      return null
    }
  }, [])

  const formatCategory = (cat) => {
    return cat.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  const formatStatus = (status) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  const formatDate = (dateStr) => {
    return format(new Date(dateStr), 'dd MMM yyyy, HH:mm')
  }

  const handleStatusUpdate = async (newStatus) => {
    if (!client) {
      alert('GraphQL client not initialized')
      return
    }

    try {
      setSubmitting(true)
      const response = await client.graphql({
        query: updateFlagStatus,
        variables: { 
          flagId: flag.flagId, 
          newStatus: newStatus 
        }
      })
      
      if (response.errors) {
        throw new Error(response.errors[0]?.message || 'Failed to update status')
      }
      
      onUpdate()
    } catch (err) {
      console.error('Error updating status:', err)
      alert(err.message || 'Failed to update status')
    } finally {
      setSubmitting(false)
    }
  }

  const handleAddNote = async (e) => {
    e.preventDefault()
    if (!newNote.trim()) {
      alert('Please enter a note')
      return
    }

    if (!client) {
      alert('GraphQL client not initialized')
      return
    }

    try {
      setSubmitting(true)
      const response = await client.graphql({
        query: addFlagNote,
        variables: {
          flagId: flag.flagId,
          content: newNote
        }
      })
      
      if (response.errors) {
        throw new Error(response.errors[0]?.message || 'Failed to add note')
      }
      
      setNewNote('')
      onUpdate()
    } catch (err) {
      console.error('Error adding note:', err)
      alert(err.message || 'Failed to add note')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="modal-overlay active" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{formatCategory(flag.category)} Concern</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="flag-meta">
            <div className="flag-meta-item">
              <div className="flag-meta-label">Date</div>
              <div className="flag-meta-value">{formatDate(flag.createdAt)}</div>
            </div>
            <div className="flag-meta-item">
              <div className="flag-meta-label">Reporter</div>
              <div className="flag-meta-value">
                {flag.isAnonymous ? (
                  <span className="badge badge-anonymous">Anonymous</span>
                ) : (
                  flag.userName || flag.userEmail
                )}
              </div>
            </div>
            <div className="flag-meta-item">
              <div className="flag-meta-label">Department</div>
              <div className="flag-meta-value">{flag.department || 'Unknown'}</div>
            </div>
            <div className="flag-meta-item">
              <div className="flag-meta-label">Severity</div>
              <div className="flag-meta-value">
                <span className={`badge badge-${flag.severity}`}>
                  {flag.severity.toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          <div className="section">
            <div className="section-title">Summary</div>
            <div className="summary-text">{flag.summary}</div>
          </div>

          <div className="section">
            <div className="section-title">Status</div>
            <div>
              <span className={`badge badge-${flag.status}`}>
                {formatStatus(flag.status).toUpperCase()}
              </span>
            </div>
            <div className="status-actions">
              <button
                className={`status-btn ${flag.status === 'new' || flag.status === 'open' ? 'active' : ''}`}
                onClick={() => handleStatusUpdate(flag.status === 'new' ? 'new' : 'open')}
                disabled={submitting}
              >
                {flag.status === 'new' ? 'New' : 'Open'}
              </button>
              <button
                className={`status-btn ${flag.status === 'under_review' || flag.status === 'in-progress' ? 'active' : ''}`}
                onClick={() => handleStatusUpdate(flag.status === 'under_review' ? 'under_review' : 'in-progress')}
                disabled={submitting}
              >
                {flag.status === 'under_review' ? 'Under Review' : 'In Progress'}
              </button>
              <button
                className={`status-btn ${flag.status === 'resolved' ? 'active' : ''}`}
                onClick={() => handleStatusUpdate('resolved')}
                disabled={submitting}
              >
                Resolved
              </button>
            </div>
          </div>

          <div className="section">
            <div className="section-title">Internal Notes (HR Only)</div>
            <form onSubmit={handleAddNote}>
              <textarea
                id="newNoteContent"
                className="note-input"
                placeholder="Add a note about this flag..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                disabled={submitting}
              />
              <button type="submit" className="add-note-btn" disabled={submitting || !newNote.trim()}>
                Add Note
              </button>
            </form>

            <div className="notes-list" style={{ marginTop: '20px' }}>
              {flag.notes && flag.notes.length > 0 ? (
                flag.notes.map(note => (
                  <div key={note.noteId} className="note-item">
                    <div className="note-header">
                      <div className="note-author">{note.authorName}</div>
                      <div className="note-date">{formatDate(note.createdAt)}</div>
                    </div>
                    <div className="note-content">{note.content}</div>
                  </div>
                ))
              ) : (
                <div className="empty-notes">No notes yet</div>
              )}
            </div>
          </div>

          {flag.statusHistory && flag.statusHistory.length > 0 && (
            <div className="section">
              <div className="section-title">Status History</div>
              <div className="history-list">
                {flag.statusHistory.map(entry => (
                  <div key={entry.historyId} className="history-item">
                    <div className="history-change">
                      <span className={`badge badge-${entry.oldStatus}`}>
                        {formatStatus(entry.oldStatus)}
                      </span>
                      <span className="history-arrow">→</span>
                      <span className={`badge badge-${entry.newStatus}`}>
                        {formatStatus(entry.newStatus)}
                      </span>
                      <span style={{ marginLeft: '12px', color: '#6b7280', fontSize: '13px' }}>
                        by {entry.changedByName}
                      </span>
                    </div>
                    <div className="history-date">{formatDate(entry.changedAt)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FlagModal

